import { createSlice } from "@reduxjs/toolkit";
import { fsDiag } from "../../utils/fsDiag";
// A leaf util — safe at the top, unlike spinSlice/gameSettingsSlice which
// have to be required lazily inside extraReducers (see the note there).
import { kindOfCommand, COMMAND_KIND } from "../../utils/spinCommands";

/**
 * Free Spins Slice
 *
 * Dedicated state for the Mad Labs free spins game: the gamble ladder that
 * decides the package, the spins it pays out, and the multiplier ladder the
 * player climbs while playing them.
 *
 * It is a free spins game with a few extras bolted on — a level ladder and a
 * collector — and nothing here forces a game to use them. Leave the level
 * reducers alone and this is a plain free spins slice.
 *
 * ── PLURAL vs SINGULAR — read this before importing ──
 *
 * featureGameSettingsSlice still carries Zeus's SINGULAR free-spin fields:
 * freeSpinActive, freeSpinCount, freeSpinIndex, freeSpinTotal and three more.
 * Those drive the OLD mask-reveal flow and are read from primary/, which is
 * out of scope for this game, so they are neither migrated nor deleted.
 *
 * Everything in this slice is PLURAL. That one letter is the whole tell:
 *
 *   state.featureGame.freeSpinActive    Zeus, legacy, primary/ only
 *   state.freeSpins.active              Mad Labs, current
 *
 *   selectFreeSpinActive()              legacy
 *   selectFreeSpinsActive()             current
 *
 * When the Zeus flow is deleted the singular set goes with it and the
 * ambiguity ends. Until then, if you are writing new code and reach for a
 * singular name, you have the wrong one.
 *
 * ── What is deliberately NOT here ──
 *
 * Popup visibility. showFreeSpinIntro, showAdditionalFreeSpins,
 * showFreeSpinTotalWin and the rest stay in animationsSlice, which is where
 * every other popup flag in this game lives — potCollectionSlice holds none
 * either. This slice answers "what is the round", not "what is on screen".
 */

// ============================================================================
// DOMAIN
// ============================================================================

/**
 * The four levels, in climbing order.
 *
 * The names are the bonus_gameview skin names, so a level IS the room it
 * wears. Each level also owns one progress box, matched by position:
 *
 *   low  ->  lvl_x2     high     ->  lvl_x5
 *   mid  ->  lvl_x3     mystery  ->  lvl_x10
 *
 * Order is all this array is for. Everything else about a level — multiplier,
 * spins awarded — comes from the server, so none of it is written down here.
 */
// In the order the ART climbs, which is the order GDD §6 climbs.
//
// The names are the spine's, and they are meaningless — the skins carry these
// colour layers:
//
//   low      no tint      mystery  gameview/lvl1
//   mid      gameview/lvl2         high     gameview/lvl3
//
// So the climb is low -> mystery -> mid -> high. The array was in name order
// instead, which walked none -> lvl2 -> lvl3 -> lvl1: the first tinted room a
// player saw was lvl2, and lvl1 only appeared at the very top.
export const FREE_SPINS_LEVELS = ["low", "mystery", "mid", "high"];

/**
 * ── The one table. Everything else in this feature indexes off it. ──
 *
 * Chronological, so the index IS the rung. The three lists line up by
 * position, which is why boxKeyForLevel is an indexOf and not a lookup:
 *
 *   index          0         1         2         3
 *   room skin      low       mid       high      mystery
 *   progress box   lvl_x2    lvl_x3    lvl_x5    lvl_x10
 *   multiplier     x2        x3        x5        x10
 *   GDD §6 level   1         2         3         4
 *
 * GDD §6 lists FIVE levels against these four rooms. Level 0 is the reason:
 * its Golden multiplier is x1, so it has no box of its own. It is the `low`
 * room with nothing filled — an entry state, not a fifth room.
 */

/**
 * Package tier → the room the round opens in.
 *
 * The server reports which package was won as a TIER (`fs_gamble`), and the
 * tier is not the level — see the header. Both independent sources agree on
 * this mapping:
 *
 *   GDD §10 buy cards   card 1 "from Level 0" · card 2 "from Level 2" ·
 *                       card 3 "from Level 4"
 *   GDD §9  gamble      the same three packages, purple / red / gold
 *
 *   tier 1  purple  4-9 spins   GDD Level 0  ->  `low`, nothing filled
 *   tier 3  red     7-9 spins   GDD Level 2  ->  `mid`     (x3)
 *   tier 5  gold    8-10 spins  GDD Level 4  ->  `mystery` (x10)
 *
 * Tier 0 is the lose rung and enters nothing. Tiers 2 and 4 do not exist,
 * because there is no entry package at Level 1 or Level 3 — those are only
 * ever reached by collecting scatters.
 *
 * CONFIRMED for tier 1 (bought level "1" returned `values: [[9,1]]`, round
 * 1415621). Tiers 3 and 5 are inferred from the two GDD sections agreeing;
 * buy cards 2 and 3 on the demo would confirm them outright.
 */
export const FREE_SPINS_TIER_TO_LEVEL = {
  1: "low", // purple — GDD level 0
  3: "mid", // red    — GDD level 2
  5: "high", // gold  — GDD level 4
};

/**
 * The room a server rung stands in.
 *
 *   fs_state.level   1     2     3     4     5
 *   GDD §6 level     0     1     2     3     4
 *   room             low   myst  mid   high  high
 *   multiplier held  x1    x2    x3    x5    x10
 *
 * The room is the level you are ON — `level - 1`, which is the GDD level
 * itself. It used to be `level - 2`, which showed the level you had just left:
 * a purple package opened on the room belonging to the rung below it, and the
 * first box filled without the room changing at all.
 *
 * GDD §6 has FIVE levels and the art has four rooms, so the top two share
 * `high`. That is the only place two rungs look alike, and it is at the ceiling
 * where there is nothing further to climb to.
 */
export const roomIndexForServerLevel = (level) =>
  Math.min(
    Math.max((typeof level === "number" ? level : 1) - 1, 0),
    FREE_SPINS_LEVELS.length - 1,
  );

/**
 * Where the gamble scatter is standing on a board.
 *
 * Derived from the BOARD rather than carried, so live entry and every reload
 * answer it the same way. The alternative — storing the coordinate the
 * provider's gambleScatter reported — only exists on the response that
 * triggered, and a refresh is a different response entirely: that is exactly
 * why a reloaded round came back with a closed gold door and nothing in it.
 *
 * The id is written out rather than imported: this slice imports no symbol
 * tables, and SC1 is fixed by the paytable — special_symbols reports
 * `{kind:"scatter", symbol:"12"}` on every response.
 *
 * @param   {Array<Array<number>>} reels
 * @returns {[number, number]|null} [reel, row]
 */
const GAMBLE_SCATTER_SYMBOL_ID = 12;

export const gambleScatterCell = (reels) => {
  if (!Array.isArray(reels)) return null;
  for (let reel = 0; reel < reels.length; reel += 1) {
    const column = reels[reel];
    if (!Array.isArray(column)) continue;
    for (let row = 0; row < column.length; row += 1) {
      if (Number(column[row]) === GAMBLE_SCATTER_SYMBOL_ID) return [reel, row];
    }
  }
  return null;
};

/**
 * Where the package was WON — the cell the card is put back on.
 *
 * The board scan above is still the answer for every round that has SC1 on it,
 * and it is still derived rather than carried for the reason it states. This
 * adds the one case it cannot see: a package won INSIDE the Golden Mystery
 * (GDD §7). There the screen holds B2 at that coordinate — the scatter is
 * standing inside a mystery window — so the scan finds nothing at all.
 *
 * The round's own `gambleScatter.position` is the fallback, and it is safe to
 * read here for a reason the scan's note does not cover: this is read off the
 * ROUND, which the trigger path stores whole (saveFreeSpinsRound), so a reload
 * gets the same coordinate the live spin did rather than a live-only field.
 *
 * A bought round still answers null — it has no board and no reveal, so there
 * is no cell and no card, which is already how the room draws it.
 *
 * @param   {object} round
 * @returns {[number, number]|null}
 */
export const triggerScatterCellFrom = (round) => {
  const onBoard = gambleScatterCell(round?.reels);
  if (onBoard) return onBoard;
  const position = round?.gambleScatter?.position;
  if (!Array.isArray(position) || position.length < 2) return null;
  const reel = Number(position[0]);
  const row = Number(position[1]);
  return Number.isFinite(reel) && Number.isFinite(row) ? [reel, row] : null;
};

/**
 * The board the free-spins round OPENS on — reels only.
 *
 * The copied trigger screen exists for exactly one reason: the scatter is
 * still standing on it, so the card can be shown and its pips can fly into the
 * level box. Everything else about that spin is finished business. It was
 * presented in the base game, it was paid there, and the balance moved for it.
 *
 * So the wins are STRIPPED. Hand the free-spins win controller a board with
 * `wins` on it and it does what it is built to do — dims the reels, draws win
 * boxes, counts up a win text — for a win the player already collected, on a
 * screen that is only there as scenery. `win: 0` and an empty `wins` is what
 * makes the whole presentation return at its first gate.
 *
 * Nothing else is copied either: no `triggerWin`, no `winType`, no
 * `mystery_feature`. A bare object cannot re-trigger anything, which is the
 * property that matters — the live path has always built it this way and only
 * the reload paths spread the round in whole.
 *
 * @param   {object} round any round carrying the trigger board
 * @returns {object|null}
 */
const arrivalBoardFrom = (round) => {
  if (!Array.isArray(round?.reels)) return null;
  return {
    reels: round.reels,
    wins: [],
    win: 0,
    // ── ...but `storage` IS copied, because the board cannot be drawn without
    //    it ──
    //
    // `reels` alone is not a picture of the screen. Two kinds of cell are
    // painted OVER the reels from `storage`, and neither of them is in the
    // symbol ids:
    //
    //   storage.mystery_feature   what each mystery became. maskReelForMystery
    //                             needs it, and FreeSpinBasicReel already
    //                             calls that — without it every mystery on the
    //                             arrival board draws as the bare `my` cover.
    //   storage.coins_feature     the golden cells. goldenFinalBoard reads
    //                             ONLY this, so with it absent the room opened
    //                             on a board with the golden frames missing
    //                             and the mystery art showing through.
    //
    // Safe against the re-trigger worry above: both fields are descriptions of
    // what already landed, read only by the two staging helpers to choose a
    // texture. Nothing keys a trigger off `storage` — the scatter counts,
    // `triggerWin` and `winType` are what do that, and those stay stripped.
    storage: round.storage ?? null,
    // The coins' numbers (Games/Shared/coinValues) — the arrival board shows
    // the same values the trigger spin landed with.
    coinValues: round.coinValues ?? null,
    // Marks this as the board we ARRIVED on rather than one a free spin
    // produced. The first `freespin` response replaces the whole object, so
    // the flag cannot outlive the moment it describes.
    isTriggerBoard: true,
  };
};

/** The room a package tier opens in, or the first room when it is unknown. */
export const levelForTier = (tier) =>
  FREE_SPINS_TIER_TO_LEVEL[tier] ?? FREE_SPINS_LEVELS[0];

/** Tears on a box's collector. Tier runs 0..4, so 0 is the empty box. */
export const FREE_SPINS_TIERS_PER_LEVEL = 4;

/**
 * Gamble tier codes, as the server sends them.
 *
 * Verified live against dev.popiplay.dev/api/MadLabs/24570 — see
 * parseGambleState in RGS/Providers/PopiPlay/RGS.js. 0 is the lose rung: land
 * on it and the package is forfeit outright.
 */
export const FREE_SPINS_TIER = {
  LOSE: 0,
  LOW: 1,
  MID: 3,
  HIGH: 5,
};

/**
 * Hydrate the slice from an init/refresh `freeSpinsState` (cold load + error
 * recovery). The provider derived the PHASE off flow.state; this only lays
 * out state to match it. No-op when no feature is in progress — same contract
 * as hydratePotCollectionFromInit.
 *
 * What is deliberately NOT set here is `active`. Being mid-feature and OWNING
 * THE SCREEN are two different things, exactly as they are on the live path:
 * the intro popup and the wipe sit between them, and the boot orchestrator
 * drives that the same way a live collect does.
 */
/**
 * The ladder, from the server's own fs_state. The ONE owner of these rules.
 *
 * fs_state.level is ONE-INDEXED against GDD's levels, and the rooms start at
 * GDD level 1: level 2 is the first room with a box. Level 1 is the entry
 * state, which shows the first room with nothing filled.
 *
 * Called for EVERY response that carries fs_state, not just a `freespin`.
 * It used to live inside the freespin branch, which meant a bought package
 * entered the room with serverLevel still 1 — so the boxes had no idea three
 * levels were already banked, and the entry flourish never armed.
 */
/**
 * Apply the server's ladder — the room and the collector.
 *
 * ── Why this can be DEFERRED ──
 *
 * The response arrives the moment the spin is requested, long before the reels
 * have stopped. Committing it here lights the next bulb while the scatter that
 * earned it is still spinning, which is the classic version of this bug: the
 * UI is ahead of the animation that is supposed to cause it.
 *
 * So on a live free spin the values are PARKED, and the win controller commits
 * them once the scatter has played `distribute` and its trail has reached the
 * box. Exactly Pot Collection's shape — rgsPlay.fulfilled stores a pending
 * respin count and `applyRoyalCollectPendingRespins` applies it when the coin
 * actually lands.
 *
 * Every other caller commits immediately, and must: a reload has no animation
 * to wait for, and a bonus-buy or collect_freespins response is the round
 * being SET UP rather than progressing. Deferring those would strand the
 * value with nothing coming to release it.
 *
 * @param {object}  state
 * @param {object}  fsState  the server's `features.fs_state`
 * @param {boolean} defer    park the values instead of committing them
 */
function applyFsState(state, fsState, defer = false) {
  if (!fsState) return;

  const hasLevel = typeof fsState.level === "number";
  // Four scatters fill a box, so the collector is the remainder.
  const hasTier = typeof fsState.total_collected_scatters === "number";
  const tier = hasTier
    ? fsState.total_collected_scatters % FREE_SPINS_TIERS_PER_LEVEL
    : null;

  // Spins the level-up itself awarded, this spin. The popup renders it as
  // "+N FREE SPINS", and it is the only place that number exists.
  const issuedByLevel = Array.isArray(fsState.fs_issued_by_levels)
    ? fsState.fs_issued_by_levels.filter((n) => typeof n === "number")
    : [];
  const issued = issuedByLevel.reduce((sum, n) => sum + n, 0);

  if (defer) {
    // Park only what actually MOVED. Most free spins repeat the same rung, and
    // parking an unchanged value made every spin look like a charge was owed:
    // the sequence found nothing on the board to explain it and committed,
    // which turned "the ladder moved with no scatter" from an alarm into
    // constant noise. Now that line means something again.
    if (hasLevel && fsState.level !== state.serverLevel) {
      state.pendingServerLevel = fsState.level;
    }

    // ── Park on a LEVEL move too, not only a tier move ──
    //
    // `tier` is `collected % 4`, so a spin that lands exactly four scatters
    // wraps to the SAME remainder — 8 and 12 collected are both tier 0. The
    // charge sequence reads `pendingTier === null` as "nothing owed", so the
    // one spin that completes a level cleanly was the one spin that showed
    // nothing: no trails, no bulbs, no level-up popup, and serverLevel never
    // committed.
    //
    // A level move is proof a charge happened whatever the remainder did.
    const tierMoved = hasTier && tier !== state.tier;
    const levelMoved = hasLevel && fsState.level !== state.serverLevel;
    if (hasTier && (tierMoved || levelMoved)) state.pendingTier = tier;
    if (issued > 0) {
      state.pendingLevelUpSpins = issued;
      // ── Kept as a LIST as well as a total ──
      //
      // One entry per level crossed, in order. The walk now announces a card
      // per box that banks, so each one needs its own number; the total alone
      // made the second box of a two-box spin repeat the first box's spins.
      state.pendingLevelUpSpinsList = issuedByLevel;
    }
    return;
  }

  // Committing directly also DROPS anything parked: this path is the newer
  // truth, and a stale pending value released later would walk the box
  // backwards.
  state.pendingServerLevel = null;
  state.pendingTier = null;
  state.pendingLevelUpSpins = 0;
  state.pendingLevelUpSpinsList = [];
  // Nothing is owed an announcement any more — this path IS the newer truth.
  state.unannouncedSpins = 0;

  if (hasLevel) {
    state.serverLevel = fsState.level;
    const roomIndex = roomIndexForServerLevel(fsState.level);
    state.level = FREE_SPINS_LEVELS[roomIndex];
  }
  if (hasTier) state.tier = tier;
}

/**
 * @param {object} state
 * @param {object} fs        freeSpinsState, as detectFreeSpinsState built it.
 * @param {object} [initRound] The init's own restored round — `result.round`,
 *   the board gameSettingsSlice hands the base game. See where it is used.
 */
/**
 * @param {object}  state
 * @param {object}  fs         the provider's freeSpinsState
 * @param {object} [initRound] the init's own round, for the arrival board
 * @param {object} [p]
 * @param {boolean} [p.coldInit] TRUE only for a real page load / reload.
 *
 * ── Why the caller has to say which it is ──
 *
 * This runs for BOTH `initializeGame` (a cold load) and `refreshGameData` (a
 * mid-session sync), because both carry the same payload. That is right for
 * restoring counters and the ladder, and WRONG for anything that re-presents:
 * refreshGameData is dispatched while the game is running — by
 * SceneTransitionOverlay on a scene change, by the win controller after a
 * celebration, by WarningPopup and by error recovery.
 *
 * So the two things that make a reload REPLAY its last spin are gated on
 * coldInit. Without the gate a refresh mid-bonus handed the win controller a
 * stale round to present, and dropped the Total Win row by a win that had
 * already been paid, with nothing left to put it back.
 */
function hydrateFreeSpinsFromInit(state, fs, initRound = null, { coldInit = false } = {}) {
  if (!fs) return;

  const data = fs.data || {};
  // ── The mystery DOES replay on reload ──
  //
  // A `withoutMystery` helper used to strip `mystery_feature` out of every
  // restored round here, so the room came back with the resolved board and no
  // reveal. Two arguments held it up and neither survives:
  //
  //   "the base game gets this for free" — it did, because saveBaseGameRound
  //   whitelisted its fields and dropped `storage`. It now keeps it on purpose,
  //   and the base game replays the reveal before the win exactly as it replays
  //   the win itself. A round that opens its doors in one game and not in the
  //   other is the inconsistency, not the fix.
  //
  //   "id null makes the once-per-round guard read it as already presented" —
  //   true of a `null` initial value, which is why both win controllers hold an
  //   UNPRESENTED/UNREVEALED SYMBOL instead. `null === null` cannot happen.
  //
  // So the round is handed over whole. The reveal's own cost control is the
  // drama tier: a board that threatens nothing opens with zero anticipation
  // loops, which is most of them.
  const lastRound = data.lastRound;

  // ── The board for a round that has not been played yet ──
  //
  // detectFreeSpinsState builds `lastRound` only when `played > 0`, and says
  // why: `outcome.screen` is null in the 0-played window. So the ONE phase
  // that most needs a board — the intro, where the player is about to arrive
  // on the screen they triggered on — is the one phase that has none.
  //
  // The provider has it anyway. initGame reconstructs the trigger round from
  // `features.trigger_screen` and publishes it as `result.round`, which is how
  // the base game gets its board back. Same object, same reels, and it carries
  // the scatter still standing where it landed.
  const arrivalRound = lastRound || initRound;
  state.reloadPhase = fs.phase;
  if (data.gamble) state.gamble = data.gamble;

  if (!data.gambleSettled) {
    // The ladder is still open and the server is waiting on an answer.
    // The package on screen must be the rung the player actually reached,
    // not the one they were first offered — the ladder itself says which.
    state.triggered = true;
    state.gambleSettled = false;
    state.awarded = data.gamble?.spins || 0;
    state.packageTier = data.gamble?.tier ?? null;
    state.played = 0;
    state.spinPlayedBase = 0;
    state.displayPlayedBase = 0;
    // No free spin has been played yet, so whatever the server reports as the
    // round total IS the trigger win — balance.game and outcome.win were the
    // same number on the captured reload. That makes the refresh rebuild it
    // without a storage key, which is the one thing Pot Collection needs one
    // for.
    state.triggerWin = data.totalWin || 0;
    state.totalWin = state.triggerWin;
    state.displayedTotalWin = state.triggerWin;
    state.lastWin = 0;
    // ── The room's arrival board and the scatter's cell, seeded HERE too ──
    //
    // This branch returns before the settled-intro fall-through below, which
    // is where a collected reload seeds both. A LIVE trigger seeds them from
    // the spin answer. An open-ladder reload — this branch — had neither: the
    // trigger presentation replays, the collect answer carries no board (its
    // screen is null, so it emits no trigger), and the room opened with
    // `triggerScatterCell` null. FreeSpinBasicReel then never painted the
    // card, the live card never mounted, and no pips flew: the cell sat there
    // as symbol 12's plain texture, the closed golden doors. Vladimir,
    // 2026-09-24: *"only reload intro cant rebuild this."* Same two lines as
    // the trigger branch and the fall-through, so all three paths agree.
    if (arrivalRound?.reels) {
      state.currentRound = arrivalBoardFrom(arrivalRound);
      state.triggerScatterCell = triggerScatterCellFrom(arrivalRound);
    }
    return;
  }

  // ── Collected: settled intro, and "in_progress" ──
  // The package is already the player's, so the gamble screen must not
  // reopen — the win replay hands straight to the intro popup.
  //
  // ── MID-ROUND is a different animal ──
  //
  // `triggered` means "won, but the round has not taken the screen yet" — the
  // window the gamble and the intro popup live in. Part-way through a round that
  // is simply false: the room IS the screen. So the two flags go the other way
  // round, and the entry sequence is skipped entirely rather than replayed.
  //
  //   triggered  false — nothing is pending; do not offer a gamble or an intro,
  //              and do not let useWinController treat lines as trigger lines.
  //   active     true  — the room owns the screen from the first frame, with no
  //              wipe. TransitionDialog reads these two with opposite signs, so
  //              this also correctly makes the NEXT transition a "leaving" one.
  //
  // The scene then puts the reload popup up over it — see GameViewSimple.
  // ── COMPLETION: the round is over and only the total win is owed ──
  //
  // Pot Collection's completion phase, same shape. The room stays on screen
  // with `active` true — that is where the player was standing, and
  // TransitionDialog reads `active` to know the next wipe is a LEAVING one, so
  // the way out works exactly as it does after a live last spin.
  //
  // Nothing is pending and nothing is replayed: no gamble, no intro, no resume
  // press. With played === awarded the round loop's own `remaining <= 0` branch
  // opens the total win dialog on its usual OUTRO_DELAY, so this needs no
  // scene code of its own.
  const completion = fs.phase === "completion";
  const midRound = fs.phase === "in_progress";
  state.triggered = !midRound && !completion;
  state.active = midRound || completion;
  state.gambleSettled = true;

  if (completion) {
    fsDiag("hydrate COMPLETION", {
      issued: data.issued,
      played: data.played,
      totalWin: data.totalWin,
      lastRoundWin: data.lastRound?.win ?? null,
      lastRoundTriggerWin: data.lastRound?.triggerWin ?? null,
      level: data.level,
    });
    state.spinComplete = true;
    state.spinning = false;
    state.awaitingResume = false;
    state.reloadPopupPending = false;
    state.awarded = data.issued || 0;
    state.played = data.played || 0;
    // A restored round is parked BETWEEN spins, so nothing is on screen to
    // count — see spinPlayedBase.
    state.spinPlayedBase = state.played;
    state.displayPlayedBase = state.played;
    state.totalWin = data.totalWin || 0;
    state.displayedTotalWin = state.totalWin;
    // The round's own stake, for the owed outro — see freeSpinOutroBet.
    state.completionBet = data.bet > 0 ? data.bet : null;
    // The cap, on THIS branch too. The tail of this function sets it and this
    // branch returns before reaching it, so a capped round restored from a
    // completion reload read `false` and opened the ordinary Total Win popup —
    // the one case the Max Win swap exists for.
    //
    // Assigning it here was only half the fix: on a COMPLETION reload the
    // response has already returned to base, so there was nothing in it to
    // recompute from and `data.winLimitReached` simply arrived undefined. The
    // RGS now supplies it — stamped into the stored record by the closing spin
    // so it carries the SERVER's flag, with a recompute from the round's own
    // stake as the fallback. See storedWinLimitReached in the PopiPlay RGS.
    state.winLimitReached = !!data.winLimitReached;
    // The room and the collector as the round ended. fs_state is gone from the
    // payload by now, so these come off the stored record — but the ROOM is
    // derived by the one rule that owns it, never assigned.
    //
    // `state.level` is a NAME ("low" | "mid" | "high" | "mystery"), not an
    // index. Assigning the record's numeric level here put a number in it, and
    // skinForLevel then fell through to its default — so a restored round
    // always dressed itself as the entry room whatever level it ended on.
    // applyFsState is the single owner of serverLevel -> room, and calling it
    // means the reload and the live path cannot disagree.
    if (typeof data.serverLevel === "number") {
      applyFsState(state, { level: data.serverLevel });
    }
    if (typeof data.tier === "number") state.tier = data.tier;
    if (lastRound) {
      // ── THE ROUND IS REPLAYED IN FULL, like any other reload ──
      //
      // This used to hand the board to arrivalBoardFrom, which keeps `reels`
      // and `storage` and throws the rest away — `wins: []`, `win: 0`, no
      // `triggerWin`. That is right for the board the room OPENS on, and wrong
      // here: this is the last free spin of a finished round, and the RGS goes
      // to real trouble to make it presentable (see the lastRound builder in
      // PopiPlay/RGS — it derives winType, isAnimatedWin, triggerWin and an id
      // precisely so the win controller will not return at `!triggerWin`).
      //
      // So the reveal replayed — it only needs `storage` — and the win never
      // did, and GameViewSimple's completion branch reads
      // `currentRound.win` to decide whether to WAIT for that replay. With the
      // win stripped it always read 0, never waited, and put the Total Win
      // dialog up on top of the mystery reveal. Vladimir, 2026-09-23: *"total
      // win appears before we finished everything... we replay entire round"*.
      //
      // The FLAG stays. `isTriggerBoard` is what FreeSpinBasicReelSet keys its
      // reels off (module-level `cachedReels`, assigned from nothing else), so
      // dropping it here would leave the room building its board from the base
      // game's round. It marks "the room did not spin this board" — true of a
      // restored one — and every consumer of it is unchanged by this.
      //
      // Cold load only, exactly as the mid-round branch splits: a mid-session
      // refresh is not a replay, and handing the win back there presents it a
      // second time over whatever is on screen.
      state.currentRound = coldInit
        ? { ...lastRound, isTriggerBoard: true }
        : arrivalBoardFrom(lastRound);
      state.triggerScatterCell = triggerScatterCellFrom(lastRound);

      // ── ...and the total goes back to what it read BEFORE that spin ──
      //
      // The counterpart of the same withholding the mid-round branch does, and
      // of displayedBalance's in initializeGame.fulfilled. `totalWin` already
      // contains this round, and the round is about to be paid again, so the
      // row would otherwise sit at the final figure while the win it belongs to
      // is still counting up. WinDisplay's syncFreeSpinsDisplayedWin puts it
      // back on the win's own edge, exactly as it does live.
      if (coldInit && (lastRound.win ?? 0) > 0) {
        state.displayedTotalWin = Math.max(0, state.totalWin - lastRound.win);
      }
    }
    return;
  }

  // The last screen, so the reels come back on the board the player left rather
  // than an idle fallback. Carries no win on purpose — see the note in
  // detectFreeSpinsState.
  // Armed here, not in a component: the loop can run before any scene mounts.
  // state.awaitingResume = midRound;
  // Same reason, for the win presentation rather than the loop — see the field.
  // state.reloadPopupPending = midRound;
  // ── Not under a Hold & Win won here (2026-10-08) ── the bonus owns the
  // screen (its own reload popup, its own press); the pack is not parked and
  // shows no popup of its own. When the bonus hands back, the free spins carry
  // on by themselves, exactly as they do live (see detectFreeSpinsState,
  // holdAndWinOwns). A reload on the trigger free spin replays it straight
  // away, and its coins open the bonus's intro.
  const parked = midRound && !data.holdAndWinOwns;
  state.awaitingResume = parked;
  // state.reloadPopupPending = parked;
  // ── ...but the "FREE SPINS n LEFT" popup still comes FIRST (Vladimir,
  // 2026-10-08: "hnw dialogs cant show up untill this is closed") ── it holds
  // the trigger free spin's presentation (its coins open the bonus's intro)
  // and the bonus's own reload popup / collection (HoldAndWinControlContext)
  // until it is dismissed. Only the parking is dropped under a bonus.
  // state.reloadPopupPending = midRound;
  // Not with the bonus already on screen (mid-respins or settled): only ITS
  // popup shows — see holdAndWinOnScreen in PopiPlay initGame.
  state.reloadPopupPending = midRound && !data.holdAndWinOnScreen;

  if (midRound) {
    // ── The spin the player left is OVER, whether or not its board came back ──
    //
    // These two were once inside the `data.lastRound` branch below, and that was
    // the stall: lastRound is only built when the init carries an
    // `outcome.screen` array, so on any reload without one, spinComplete stayed
    // false. The loop's second gate is `if (spinning || !spinComplete) return;`,
    // so it returned on every pass for ever — the button glowed, the press
    // cleared the gate, the effect re-ran, hit this same gate and did nothing.
    // Nothing else was ever going to change, so the round sat there.
    //
    // The flags describe the ROUND's phase, not whether we recovered a picture
    // of it: a part-played round is always between spins by definition. The
    // board is a separate concern and has its own idle fallback.
    state.spinComplete = true;
    state.spinning = false;

    // What the restored round actually carries. The win presentation needs
    // `triggerWin` AND a non-empty `wins` — it returns early on either, and both
    // returns look identical from the outside.
    fsDiag("hydrate IN_PROGRESS", {
      coldInit,
      hasLastRound: !!lastRound,
      win: lastRound?.win ?? null,
      wins: Array.isArray(lastRound?.wins) ? lastRound.wins.length : null,
      triggerWin: lastRound?.triggerWin ?? null,
      winType: lastRound?.winType ?? null,
      id: lastRound?.id ?? null,
      hasStorage: !!lastRound?.storage,
      mystery: !!lastRound?.storage?.mystery_feature,
      coins: Array.isArray(lastRound?.storage?.coins_feature)
        ? lastRound.storage.coins_feature.length
        : null,
      totalWin: data.totalWin ?? null,
      // What the row will read until the win is presented — see the
      // withholding below.
      displayedFrom: Math.max(0, (data.totalWin ?? 0) - (lastRound?.win ?? 0)),
    });

    // Part-played: this is a free spin's own screen. The arrival block in the
    // tail handles played === 0 for every phase, including this one.
    //
    // The WIN rides along only on a cold load, where the point is to replay the
    // spin. On a mid-session refresh the board is restored and the win is
    // stripped: the player has already watched it pay, and handing it back with
    // `triggerWin` set makes the win controller present it again over whatever
    // is on screen.
    if (lastRound) {
      state.currentRound = coldInit
        ? lastRound
        : { ...lastRound, wins: [], win: 0, triggerWin: false };
    }
  }
  state.awarded = data.issued || 0;
  state.played = data.played || 0;
  // Parked between spins — see spinPlayedBase.
  state.spinPlayedBase = state.played;
  state.displayPlayedBase = state.played;
  state.totalWin = data.totalWin || 0;
  // Recomputed from the response, never restored from storage — see the field.
  state.winLimitReached = !!data.winLimitReached;
  // The displayed total starts level with the real one — every phase but the
  // one below, where the last spin is about to be paid again.
  state.displayedTotalWin = state.totalWin;

  // ── Mid-round: WITHHOLD the restored spin's win so it can be paid again ──
  //
  // The exact counterpart of the displayedBalance withholding in
  // initializeGame.fulfilled, and for the same reason: the server's total
  // ALREADY contains this spin, and the reload is about to replay the spin. Left
  // level, the player watched the mystery open, the coins land and the
  // multiplier hit, and the Total Win never moved — it had been paid before the
  // reveal it belongs to had even started.
  //
  // So the row goes back to what it read BEFORE the restored spin (429 - 60 =
  // 369 on the round this was found on), and the presentation puts it back:
  // WinDisplay dispatches syncFreeSpinsDisplayedWin on the win's own
  // false->true edge, which assigns `totalWin` — the same edge, the same
  // action and the same result as a live free spin. That is what makes this
  // behave like any other win rather than like a restore.
  //
  // `totalWin` itself is NOT touched. It is the round's real figure, the spin
  // loop and the total-win dialog read it, and withholding it would end the
  // round on the wrong number.
  //
  // Only when there is a win to withhold. A losing spin leaves the row alone,
  // and a presentation that never runs self-corrects on the next win, which
  // re-snaps to the then-current total.
  if (coldInit && midRound && (lastRound?.win ?? 0) > 0) {
    state.displayedTotalWin = Math.max(0, state.totalWin - lastRound.win);
  }
  if (data.gamble?.tier != null) state.packageTier = data.gamble.tier;

  // ── The arrival board, for every entry path there is ──
  //
  // `played === 0` IS the arrival: the round is recovered before its first
  // spin, so the trigger board is still the board and the scatter is still on
  // it. Part-played is deliberately excluded — there `lastRound` is a free
  // spin's own screen, the scatter is long gone, and flagging it would mount a
  // card on a cell that now holds some other symbol.
  //
  // This is the settled-intro fall-through: neither `completion` nor
  // `in_progress`, so neither branch above ran and nothing had seeded a board
  // at all. That is why a reload into the intro phase came back with the room
  // showing whatever FreeSpinBasicReelSet had cached at load.
  if ((data.played || 0) === 0 && arrivalRound?.reels) {
    state.currentRound = arrivalBoardFrom(arrivalRound);
    state.triggerScatterCell = gambleScatterCell(arrivalRound.reels);
  }

  // Room and collector, read exactly as a live `freespin` reads them —
  // see the fs_state block in rgsPlay.fulfilled, the one owner of these rules.
  applyFsState(state, data.fsState);

  // ── The package the arrival card must wear ──
  //
  // packageTier comes off `fs_gamble`, and the server stops sending a current
  // rung once the package is COLLECTED — which is precisely the state a
  // reload into the intro phase is in. So the tier is missing exactly when the
  // card needs it, and the cell would sit there as a closed door.
  //
  // The level supplies it. On the arrival board no scatter has been collected
  // yet, so the round is standing on its ENTRY level, and the entry levels are
  // the three packages: server level 1 / 3 / 5 are GDD levels 0 / 2 / 4, which
  // are tiers 1 / 3 / 5. Those are the same numbers, and only because they are
  // entry levels — the moment a spin is played this no longer holds, which is
  // why it is gated on the arrival board.
  if (state.triggerScatterCell && state.packageTier == null) {
    const entryLevel = state.serverLevel;
    if (entryLevel === 1 || entryLevel === 3 || entryLevel === 5) {
      state.packageTier = entryLevel;
    }
  }
}

const initialState = {
  // ── Lifecycle ──
  // triggered: the package is won but the round has not taken the screen yet
  //            (the gamble and the intro popup live in this window)
  // active:    the free spins scene owns the screen
  // spinning:  one free spin is in flight — the reels are physically moving
  // spinComplete: that spin has been fully PRESENTED — reels landed and the
  //            win controller has finished with it. The pair brackets one
  //            round, and they are this feature's own: the base game keeps
  //            spin.isSpinning / spin.spinComplete for itself, and neither
  //            side may move the other's. See FreeSpinBasicControlContext.
  triggered: false,
  active: false,
  spinning: false,
  spinComplete: true,
  // The player asked for THIS spin to land now. The base game keeps the same
  // flag on spin.manualStop, but that reducer refuses unless spin.isSpinning —
  // which is false for the whole round, because a free spin is not a base spin.
  // The reel controller reads both, so the landing sounds the same either way.
  manualStop: false,

  // ── The package ──
  // What the gamble settled on, and how much of it is used up.
  awarded: 0,
  played: 0,
  /**
   * `played` as it stood when the spin ON SCREEN began — the button's counter.
   *
   * `played` is the server's count of spins already RESOLVED, and it grows the
   * instant the response lands. So `awarded - played` stops counting the spin
   * the player is watching: on the last one it read 0 with the reels still
   * turning, which is what "it said 0 and I had 1 more round" was.
   *
   * Latched at spin start instead. There, the starting spin is not in `played`
   * yet, so `awarded - this` is the spins remaining INCLUDING the one about to
   * play, and it holds that number until the next spin begins.
   *
   * A base rather than a copy of the whole count, so spins awarded mid-round —
   * a level completing hands over three — appear the moment the server sends
   * them rather than at the next spin.
   *
   * Same shape as displayedTotalWin: a presentation-timed twin of a server
   * number, never the number itself.
   */
  spinPlayedBase: 0,
  // ── The BUTTON's base. The two count different things ──
  //
  //   spinPlayedBase   what the SERVER has confirmed. The loop spins while
  //                    this leaves spins owed, so it may never run ahead.
  //   displayPlayedBase what the PLAYER has spent. A spin that has STARTED is
  //                    already spent, so this leads by one while one is
  //                    running — see setFreeSpinsSpinning.
  //
  // The button therefore reads "spins left, not counting the one you are
  // watching": 9 in the room, 8 as the first spin goes, 0 as the last one
  // starts, then the Total Win. One field could not do both jobs — counting a
  // spin early on the LOOP's base ends the round a spin short, and counting it
  // late on the BUTTON leaves the first spin reading 9 and the last stuck on 1.
  // Both were reported, in that order.
  //
  // Mirrors spinPlayedBase at every other write. The ONE exception is the
  // end-of-round catch-up in rgsPlay.fulfilled, which is the loop's alone.
  displayPlayedBase: 0,
  // ── The LIVE round, parked while a replay borrows this slice ──
  //
  // A replayed free-spins round writes straight into these fields: active,
  // awarded, played, the ladder, the board. gameSettingsSlice and
  // freeRoundsSlice both snapshot themselves on session entry and hand it back
  // on exit; this slice did not, so a replayed round's state SURVIVED the exit.
  //
  // That is a ban path, not just a cosmetic leak. `active` left true keeps the
  // free-spins loop running, and the moment the RGS object leaves replay mode
  // its next `freespin` goes LIVE — to a session that is not in free spins.
  // The server answers 202, and the second one locks the player out.
  //
  // Null when not replaying. Snapshot on the FIRST start only: "Replay Again"
  // dispatches startReplaySession a second time and would otherwise capture
  // replay data as if it were the live round.
  preReplaySnapshot: null,
  // Which package the gamble settled on — one of FREE_SPINS_TIER, or null.
  // NOT the level tier below; that is a different ladder entirely.
  packageTier: null,

  // ── The level ladder ──
  // Which room we are in, and how full its box is. Two fields, and the whole
  // scene is derived from them.
  level: "low",
  tier: 0,

  // ── The ladder the server has already moved, but the player has not seen ──
  //
  // Parked by applyFsState on a live free spin and released by
  // commitFreeSpinsProgress once the scatter's trail has hit the box. null
  // means nothing is owed. See applyFsState for why this exists.
  pendingTier: null,
  pendingServerLevel: null,
  // Spins awarded by the level-up that is pending. Drives the popup's label.
  pendingLevelUpSpins: 0,
  /** One entry per level crossed this spin — see pendingLevelUpSpins. */
  pendingLevelUpSpinsList: [],
  // ── Spins the server has given but the player has not been TOLD about ──
  //
  // The counter on the play button is a promise about what is left, and a
  // level-up quietly rewriting it the instant the response lands breaks the
  // whole beat: the number climbed while the board was still settling, seconds
  // before the popup that announces it. So the increase is held here and the
  // button subtracts it, until the room re-dresses under the popup — the same
  // moment, and the same beat, as every other part of the level-up.
  //
  // `awarded` itself is NOT held. It is the round's real length and the spin
  // loop reads it: holding that would open the total-win screen on the very
  // spin that earned more. Only the DISPLAY waits, which is all that was wrong.
  //
  // Held for EVERY way a round can add spins — a level-up's reward and the
  // feature's own +FS cells — because one popup announces them together, and
  // that popup is the release. See announceFreeSpins. A hold that is somehow
  // never announced is dropped when the next response lands, so the button can
  // read low for one spin at worst.
  unannouncedSpins: 0,
  // ── The server's own rung, unmodified ──
  //
  // `level` above is the ROOM skin and `tier` the dots, but the PROGRESS BOXES
  // climb on a different index and cannot be derived from either. Both come off
  // fs_state.level, two apart:
  //
  //   fs_state.level   1      2      3      4      5
  //   room             low    myst   mid    high   high      (level - 1)
  //   boxes locked     0      1      2      3      4         (level - 1)
  //   multiplier held  x1     x2     x3     x5     x10
  //
  // Checked against a live round: total_collected_scatters 9 at level 3 is
  // floor(9/4) = 2 boxes filled with 1 tear over, which is `level - 1` locked
  // and the dots on the box ABOVE them. Deriving the boxes from the room name
  // showed one box too few.
  //
  // Stored raw rather than pre-mapped so the two ladders stay independent.
  serverLevel: 1,
  // The entry flourish: boxes already banked convert together when the room
  // opens, and the FIRST SPIN WAITS for them. True from the transition midpoint
  // until BonusGameView reports the last conversion finished — not a timeout.
  //
  // False when nothing is banked (an ordinary level-1 trigger), so that round
  // starts exactly as it did before.
  entrySequencePending: false,
  // ── A mid-round reload does NOT resume on its own ──
  //
  // Set the moment the slice hydrates into "in_progress", so it is already true
  // on the first tick — before the scene has mounted, and long before the reload
  // popup exists. Without that the loop saw active + spinComplete and fired a
  // spin in the background while the popup was still up.
  //
  // Cleared by the free-spin PLAY BUTTON, which is the same contract Pot
  // Collection has: its loop never self-starts either, and the first press is
  // what begins it (see startAutoplay / autoplayStartedRef).
  awaitingResume: false,

  // ── The reload popup's claim on the screen ──
  //
  // True from the moment the init says the round is mid-play, and armed HERE
  // rather than by the scene for a reason: the popup is only raised once
  // `showGameAfterTransition` flips, which the BOOT INTRO does, so between the
  // reelset becoming ready and that flip there is a wide window with the popup
  // not yet on screen. A restored base-game win presenting in that window is
  // what put a big win on top of the reload screen.
  //
  // Read by both win controllers, which hold their whole presentation while it
  // is true — the same thing they already do for `introPopupActive`. Cleared by
  // the reload popup's own close, so the win presents THEN.
  reloadPopupPending: false,

  // ── The round's own board ──
  //
  // The free-spin board is kept HERE, not read from game.currentRound.
  // game.currentRound is overwritten by every response including ours, so the
  // base board would follow the bonus board around behind the scenes and be
  // showing the wrong screen the moment the round ends. Two boards, two
  // rounds — the same reason the bonus game has its own reel refs.
  //
  // Same shape as game.currentRound: { reels, wins, win, bet, id, ... }.
  currentRound: null,

  // ── Win ──
  totalWin: 0, // cumulative across the whole round
  lastWin: 0, // the most recent single free spin
  // What the TOTAL WIN row is allowed to show.
  //
  // `totalWin` above is rolled up the moment the response lands, which is
  // while the reels are still spinning — reading it directly would print this
  // spin's win before the player has seen it land. This one is snapped forward
  // only once the spin has been PRESENTED, which is the same split Free Rounds
  // makes with freeRounds.displayedWin.
  //
  // Snapped, never accumulated: `totalWin` is already authoritative, so
  // assigning it cannot drift and comes back correct after a reload.
  displayedTotalWin: 0,

  // ── The win cap ──
  //
  // Zeus's rule, kept deliberately (Pot Collection, GDD §18.2): reaching the
  // cap does NOT end the round. The remaining spins are played out; what
  // changes is that the counter stops at the cap and the MAX WIN popup
  // replaces the TOTAL WIN popup at the end.
  //
  // Detected in RGS.detectFreeSpinsState — server flag first, then
  // totalWin >= bet x MAX_WIN_MULTIPLIER as the backup. Never persisted: the
  // fallback reads balance.game, which IS totalWin, so a reload recomputes the
  // same answer from the response alone.
  winLimitReached: false,

  // ── The base win that carried the player in ─────────────────────────────
  //
  // A trigger spin can pay a line win AND land the scatters. That win belongs
  // to the round the player is about to enter: the server folds it into
  // `balance.game` before the first free spin, and `balance.game` IS our
  // totalWin. Verified on round 1426501 — outcome.win 2, balance.game 2, with
  // the ladder still open and no free spin played yet.
  //
  // Held separately because the presentation needs it in a window where the
  // total cannot supply it. From the scatters landing until the round opens,
  // criticalAnimationPlaying suppresses the Win row completely, so the win
  // simply vanishes — the player is carried into a bonus with nothing shown.
  // Pot Collection solved this years ago with StorageManager.saveTriggerWin
  // and WinDisplay's isBonusTriggerWinMode; this is the same idea kept in the
  // slice, where a reload can rebuild it instead of a storage key.
  //
  // Frozen at the trigger and never advanced — free spin wins go to totalWin.
  triggerWin: 0,

  // [reel, row] of the gamble scatter on the trigger board, or null.
  // See where it is written for why the tier is deliberately not kept here.
  triggerScatterCell: null,

  // ── Gamble ──
  // The parsed shape RGS puts on every result that carries one:
  //   { values, variants, current, spins, tier, canGamble, lost }
  // Null when the response has no gamble at all, which is how a caller tells
  // "no gamble" from "a gamble with nothing left to win".
  gamble: null,
  // True once the player has taken the package — collected, or lost it.
  gambleSettled: false,

  // ── Reload recovery ──
  //
  // Which part of the feature a refresh landed in, or "none" on a normal
  // boot. Written ONLY by the initializeGame hydrate below, from the phase the
  // provider derived off flow.state.
  //
  // Same job potCollection.reloadPhase does: the slice records what happened,
  // and the scene component reads it and puts the matching screen up. Nothing
  // else in the feature branches on it.
  //
  //   "none"        normal boot, no feature in progress
  //   "intro"       the round has not started — the whole entry replays and
  //                 the gamble screen is the one gate in. Whether the ladder
  //                 is still open is DATA (`gambleSettled`), not a phase.
  //   "in_progress" mid-round
  reloadPhase: "none",
  // The STAKE a round restored in its COMPLETION phase was played at. Set only
  // by the completion hydrate, cleared with the slice (resetFreeSpins after the
  // outro, and the wipe on every base spin), so a later live round can never
  // read it. See freeSpinOutroBet (3 Orbs of Zeus 9f01aeb8).
  completionBet: null,
};

const freeSpinsSlice = createSlice({
  name: "freeSpins",
  initialState,
  reducers: {
    // ── Lifecycle ──

    // ── The counters below are the SERVER's ──
    //
    // awarded / played / totalWin all come off the response
    // (`freespins_issued` grows mid-round; `freespins_left` is authoritative),
    // and rgsPlay.fulfilled is their one writer. These reducers exist for the
    // dev panel and for tests. Dispatching one during a live round double-
    // counts. `startFreeSpins` used to live here too and was DELETED: it had
    // no caller and set `gambleSettled: true` where the live trigger path sets
    // it false, so anyone reaching for it would have skipped the gamble.
    setFreeSpinsTriggered: (state, action) => {
      state.triggered = action.payload;
    },

    setFreeSpinsActive: (state, action) => {
      state.active = action.payload;
      // Entering the round clears the trigger — the two are never both true.
      if (action.payload) state.triggered = false;
    },

    setFreeSpinsSpinning: (state, action) => {
      state.spinning = action.payload;
      // A spin beginning is the one moment the counter may move — see
      // spinPlayedBase. `played` does not yet include this spin, so the
      // difference counts it.
      if (action.payload) {
        state.spinPlayedBase = state.played;
        // ── The BUTTON counts what is LEFT, not counting this spin ──
        //
        // Hence `+ 1`: the spin now starting is already spent as far as the
        // player is concerned. 9 awarded reads 9 in the room, 8 the moment the
        // first one goes, and 0 as the last one starts — so the round ends on
        // zero and the Total Win follows it.
        //
        // The loop's own base cannot do this. It must not count a spin before
        // the server has confirmed it, or the round would end one spin short.
        // That is the whole reason the two are separate fields.
        state.displayPlayedBase = state.played + 1;
      }
    },

    /**
     * The base game's spin.manualStop, for a free spin.
     *
     * FreeSpinBasicReelController reads this at the moment it is asked to stop
     * and uses it exactly as the base reel uses spin.manualStop: quick_stop
     * instead of reel_stop, and no landing-peak sound. Without it every free
     * spin landed with the full unhurried sound whether or not the player had
     * pressed, which is most of why the press felt ignored.
     */
    setFreeSpinsManualStop: (state, action) => {
      state.manualStop = !!action.payload;
    },

    /**
     * The free-spin twin of spin.spinComplete.
     *
     * Lowered by FreeSpinBasicControlContext when a spin is requested, raised
     * by the LAST reel to stop (FreeSpinBasicReelController). Everything that
     * has to wait for a spin to be over — the win controller's main effect,
     * the round's own loop — gates on this edge.
     */
    /** Armed at the midpoint, cleared when the last box has converted. */
    /** Cleared by the play button — the press that resumes a reloaded round. */
    setFreeSpinsAwaitingResume: (state, action) => {
      state.awaitingResume = !!action.payload;
    },

    /** Cleared by the reload popup's close, which releases the held win. */
    setFreeSpinsReloadPopupPending: (state, action) => {
      state.reloadPopupPending = !!action.payload;
    },

    setFreeSpinsEntrySequencePending: (state, action) => {
      state.entrySequencePending = !!action.payload;
    },

    setFreeSpinsSpinComplete: (state, action) => {
      state.spinComplete = action.payload;
    },

    // ── The package ──

    /** One free spin has finished. */
    freeSpinPlayed: (state) => {
      state.played += 1;
    },

    setFreeSpinsPlayed: (state, action) => {
      state.played = action.payload;
      state.spinPlayedBase = state.played;
      state.displayPlayedBase = state.played;
    },

    /**
     * Retrigger — more spins on top of the package.
     * This is what the Additional Free Spins popup is announcing.
     */
    addFreeSpins: (state, action) => {
      state.awarded += action.payload || 0;
    },

    // ── The level ladder ──

    /**
     * Which room we are in. Resets the tier — a level starts empty.
     * @param {string} payload One of FREE_SPINS_LEVELS.
     */
    setFreeSpinsLevel: (state, action) => {
      if (!FREE_SPINS_LEVELS.includes(action.payload)) return;
      state.level = action.payload;
      state.tier = 0;
    },

    /**
     * How full the current level's box is.
     * @param {number} payload 0..4.
     */
    setFreeSpinsTier: (state, action) => {
      const tier = action.payload;
      if (typeof tier !== "number") return;
      state.tier = Math.min(Math.max(tier, 0), FREE_SPINS_TIERS_PER_LEVEL);
    },

    /**
     * Release the ladder the server already moved.
     *
     * Called when the scatter's trail reaches the box, so the bulb lights
     * because of the animation the player just watched rather than because a
     * response arrived. The SERVER's value is what lands — the sequence may
     * have stepped the box up one bulb at a time on the way here, and this is
     * what reconciles that walk with the truth, including the wrap to 0 when a
     * level completes.
     *
     * A no-op when nothing is parked, so it is safe to call unconditionally at
     * the end of a sequence.
     */
    commitFreeSpinsProgress: (state) => {
      if (state.pendingTier !== null) state.tier = state.pendingTier;
      state.pendingTier = null;
    },

    /**
     * Re-dress the room, and nothing else.
     *
     * The level-up is shown in three beats, and each one moves a different
     * piece of state so none of them can run early:
     *
     *   popup reaches idle  -> commitFreeSpinsRoomLevel   (the colours change
     *                          under a popup that is already covering them,
     *                          instead of snapping in the player's face)
     *   popup closes        -> the box plays its conversion
     *   conversion ends     -> commitFreeSpinsServerLevel (the box locks and
     *                          the next one starts filling)
     *
     * `pendingServerLevel` is deliberately NOT cleared here — the last beat
     * still needs it.
     */
    commitFreeSpinsRoomLevel: (state, action) => {
      // The payload wins, because the walk commits the RUNG before the popup
      // is shown — by then pendingServerLevel has been cleared and only the
      // caller still knows which room was won.
      const level = action?.payload ?? state.pendingServerLevel;
      if (level === null || level === undefined) return;
      const roomIndex = roomIndexForServerLevel(level);
      state.level = FREE_SPINS_LEVELS[roomIndex];
      // ── And the counter, on the SAME frame as the colours ──
      //
      // This is the beat the player is shown the new spins: the popup is up and
      // saying "+N FREE SPINS" while the room changes behind it. The button has
      // to agree with the popup at that moment — before, and it announced the
      // news early; after, and it lags the thing announcing it.
      state.unannouncedSpins = 0;
    },

    /**
     * Tell the player about the spins the round added.
     *
     * The counter's release, for a round that awarded spins with NO level-up:
     * the Golden Mystery's own +FS cells at the top of the ladder, where the
     * server reports no new level and `commitFreeSpinsRoomLevel` is therefore
     * never dispatched.
     *
     * Dispatched from the same beat as the room change — the popup's `onIdle`,
     * with the card on screen saying "+N FREE SPINS" — so the button and the
     * popup agree on the frame the player reads them both.
     *
     * Idempotent, and safe to call when nothing is held: the walk fires it on
     * every path out, because a hold nothing releases is worse than no hold.
     */
    announceFreeSpins: (state) => {
      state.unannouncedSpins = 0;
    },

    /**
     * Move the rung. Locks the box that just filled and points the tears at
     * the next one — so it runs LAST, after the flourish has played.
     */
    commitFreeSpinsServerLevel: (state, action) => {
      const level = action?.payload ?? state.pendingServerLevel;
      if (level === null || level === undefined) return;
      state.serverLevel = level;
      state.pendingServerLevel = null;
      state.pendingLevelUpSpins = 0;
      state.pendingLevelUpSpinsList = [];
      // ── unannouncedSpins is deliberately NOT cleared here ──
      //
      // This reducer looks like the end of the level-up and is not: the walk
      // dispatches it BEFORE the popup, not after — see the comment on
      // commitFreeSpinsRoomLevel, which says the same thing about
      // pendingServerLevel being gone by then. Clearing the counter here let the
      // button climb a beat early, which is the exact fault the field exists to
      // fix. Only the room commit releases it, because only the room commit is
      // the moment the player is told.
    },

    // ── Win ──

    /**
     * Record one free spin's win and roll it into the running total.
     *
     * @internal Dev panel and tests only. The SERVER owns totalWin during a
     * live round — see the balance.game assignment in rgsPlay.fulfilled, and
     * freeRoundsSlice's identical win / displayedWin split. Dispatching this
     * mid-round double-counts.
     */
    addFreeSpinsWin: (state, action) => {
      const win = action.payload || 0;
      state.lastWin = win;
      state.totalWin += win;
    },

    /** Set the running total outright — reload, or a server-supplied total. */
    /**
     * The round hit the 10,000x cap INSIDE a Hold & Win won here (2026-10-06).
     * The cap arrives on the respins, which this slice never reads — so the
     * exit back to free spins tells it: no free spin is sent any more, and
     * the room's ending is the Max Win (isFreeSpinsCapReached).
     */
    setFreeSpinsWinLimitReached: (state) => {
      state.winLimitReached = true;
    },

    setFreeSpinsTotalWin: (state, action) => {
      state.totalWin = action.payload || 0;
      // Never leave the displayed figure ahead of the real one.
      if (state.displayedTotalWin > state.totalWin) {
        state.displayedTotalWin = state.totalWin;
      }
    },

    /**
     * Let the TOTAL WIN row catch up — called once a spin has been presented.
     *
     * WinDisplay dispatches this at the same moment it reveals the per-spin
     * "Win:" figure, so the two move together and neither can spoil the reels.
     * Free Rounds increments its own displayed total at exactly this point; the
     * only difference here is that our real total is already correct, so this
     * snaps instead of adding.
     */
    syncFreeSpinsDisplayedWin: (state) => {
      state.displayedTotalWin = state.totalWin;
    },

    // ── Gamble ──

    /**
     * Absorb a parsed gamble state.
     *
     * Normally arrives on its own from rgsPlay below; this exists for the
     * paths that do not go through a spin.
     */
    setGambleState: (state, action) => {
      state.gamble = action.payload || null;
    },

    setGambleSettled: (state, action) => {
      state.gambleSettled = action.payload;
    },

    // ── Reset ──

    /**
     * End the round — but keep the TOTAL on screen.
     *
     * potCollectionSlice.resetPotCollection preserves `currentRound` for
     * exactly this reason: the wipe carries the player back to the base game
     * and the Total Win row must still read the feature's total until they
     * spin again. Ours preserves the two totals instead, because that is where
     * our row reads from. Free rounds does the same with `displayedWin`
     * (see its own reset, which restores preservedWin).
     */
    resetFreeSpins: (state) => {
      const preservedTotal = state.totalWin;
      const preservedDisplayed = state.displayedTotalWin;
      // The parked live round survives every wholesale reset, or the exit
      // restore silently becomes a no-op. freeRoundsSlice learned this the
      // hard way — see clearFreeRounds there.
      const preservedSnapshot = state.preReplaySnapshot;
      Object.assign(state, initialState);
      state.totalWin = preservedTotal;
      state.displayedTotalWin = preservedDisplayed;
      state.preReplaySnapshot = preservedSnapshot;
    },

    /**
     * Clear the preserved total — the next BASE spin, and nothing else.
     * The twin of clearPotCollectionWin / clearFreeRoundsWin, dispatched from
     * the same place in WinDisplay.
     */
    clearFreeSpinsWin: (state) => {
      state.totalWin = 0;
      state.displayedTotalWin = 0;
      state.triggerWin = 0;
    },
  },

  extraReducers: (builder) => {
    // Required inline, not imported at the top — gameSettingsSlice pulls the
    // store in and a top-level import closes the loop. Same reason
    // potCollectionSlice does it this way.
    //
    // rgsPlay belongs in here for the SAME reason and was the one left at the
    // top of the file. spinSlice imports gameSettingsSlice, which imports
    // spinSlice, so the top-level form only resolved because store.js happens
    // to import spinSlice first: swap those two lines and this slice received
    // `undefined` and threw at boot on builder.addCase.
    const { rgsPlay } = require("./spinSlice");
    const { initializeGame, refreshGameData } = require("./gameSettingsSlice");
    // ── Park the live round while a replay borrows this slice ──
    //
    // Matched by ACTION TYPE STRING, not by importing replaySlice's creators.
    // This file already requires its siblings lazily to avoid an import cycle,
    // and a string cannot create one at all. Same reason as the ROUND_OVER
    // matcher further down.
    //
    // Everything EXCEPT the snapshot itself is parked: a replayed round writes
    // the board, the counters, the ladder and `active`, and every one of them
    // must be the live round's again on exit. Leaving `active` true is the ban
    // path — the loop keeps spinning and its next command goes out live.
    builder.addCase("replay/startReplaySession", (state) => {
      if (state.preReplaySnapshot) return; // "Replay Again" must not re-park
      const snapshot = {};
      for (const key of Object.keys(state)) {
        if (key === "preReplaySnapshot") continue;
        snapshot[key] = state[key];
      }
      state.preReplaySnapshot = snapshot;
    });

    builder.addCase("replay/endReplaySession", (state) => {
      const snapshot = state.preReplaySnapshot;
      if (!snapshot) return;
      for (const key of Object.keys(snapshot)) state[key] = snapshot[key];
      state.preReplaySnapshot = null;
    });

    // ── Reload recovery ──────────────────────────────────────────────────
    //
    // Same hook Pot Collection uses — initializeGame.fulfilled feeding a
    // hydrate — and the same division of labour: the provider decides the
    // PHASE, the slice only lays out state to match it. Without this an
    // `init` was invisible here: a refresh after a trigger dropped the player
    // into the base game with awarded 0 while the server still held the
    // package.
    builder.addCase(initializeGame.fulfilled, (state, action) => {
      hydrateFreeSpinsFromInit(
        state,
        action.payload?.result?.freeSpinsState,
        action.payload?.result?.round,
        // The real thing: a page load. This is the only caller allowed to make
        // the round replay its last spin.
        { coldInit: true },
      );
    });

    // Same hydrate as cold load — ErrorRecovery uses refreshGameData, not
    // initializeGame, and both run the provider's initGame, so the payload
    // carries the same freeSpinsState. Exactly potCollectionSlice's pairing;
    // its completion guard has no equivalent here because no free-spins phase
    // depends on data a refresh cannot rebuild.
    builder.addCase(refreshGameData.fulfilled, (state, action) => {
      hydrateFreeSpinsFromInit(
        state,
        action.payload?.result?.freeSpinsState,
        action.payload?.result?.round,
        // NOT a reload. Dispatched mid-session — a scene change, a celebration,
        // a warning popup, error recovery — so it restores and never replays.
        { coldInit: false },
      );
    });

    builder.addCase(rgsPlay.fulfilled, (state, action) => {
      // ── A BASE SPIN INVALIDATES THE PREVIOUS FREE-SPINS GAME. All of it. ──
      //
      // The rule, stated plainly: a free-spins game ALWAYS begins from a base
      // game trigger, and a second one has nothing whatever to do with the
      // first. So the moment a base spin's response lands, every field in this
      // slice goes back to its initial value — before anything below reads or
      // writes a thing.
      //
      // Why it is needed, and why a targeted guard was the wrong shape.
      //
      // This slice keeps state that deliberately OUTLIVES its round:
      // `resetFreeSpins` preserves `totalWin` and `displayedTotalWin` so the
      // Total Win row still reads after the round ends, and `currentRound`
      // holds the last board. Every one of those is correct until the next
      // free-spins game starts, and then every one of them is a lie.
      //
      // Clearing them was spread across places that each only fire for some
      // entries — a UI effect on spin start, a trigger block, a reset on the
      // way out — and a consecutive game found at least one of them had not
      // run, so the previous round's total came in with it. Chasing which one
      // is the wrong question; the invariant is that NOTHING survives a base
      // spin, so it is asserted in one place instead.
      //
      // A bought bonus is a trigger like any other — three scatters on the
      // board, `freeSpinsTrigger` emitted, the block below runs. It is not a
      // special case and is not treated as one: the wipe covers both entries
      // identically, which is the point.
      //
      // Keyed on the response's OWN command, which is the same signal the
      // storage clear uses (see the base-spin clear in PopiPlay/RGS): `spin`
      // is BASE and that includes the buy, while `freespin` is FEATURE and
      // `gamble_freespins` / `collect_freespins` are DECISION — so a round in
      // progress, and the ladder it is climbing, are never touched.
      //
      // Everything this response goes on to establish — fs_state, the trigger,
      // the package taken — is therefore built on a clean slate, in the same
      // reducer call.
      if (kindOfCommand(action.payload?.command) === COMMAND_KIND.BASE) {
        // See resetFreeSpins: the parked live round outlives this wipe.
        const preservedSnapshot = state.preReplaySnapshot;
        Object.assign(state, initialState);
        state.preReplaySnapshot = preservedSnapshot;
      }

      // ── The ladder, before anything branches on the command ──
      //
      // fs_state describes the round as it stands and is authoritative on every
      // response that carries it — the bonus-buy spin, collect_freespins, and
      // each freespin alike. Reading it only inside the freespin branch left a
      // bought package entering the room at serverLevel 1.
      // Deferred only for a live free spin — see applyFsState. Every other
      // command that carries fs_state is the round being set up, and has no
      // scatter animation coming to release the value.
      applyFsState(
        state,
        action.payload?.features?.fs_state,
        action.payload?.command === "freespin",
      );

      // ── The trigger ──
      //
      // RGS puts this on the spin that triggered and on NO other response, so
      // there is no risk of a later spin re-arming a round already running.
      // It is what every gate in useWinController now reads.
      //
      const trigger = action.payload?.freeSpinsTrigger;
      if (trigger) {
        state.triggered = true;
        // ── The room opens on the board that triggered it ──
        //
        // Not decoration: the scatters that won the round have to still be
        // standing there when it starts.
        //
        // Without this the board was whatever FreeSpinBasicReelSet had cached
        // — and that cache is a module-level `let`, filled from game
        // .currentRound the first time BonusGameView renders. BonusGameView
        // mounts at loaderComplete and merely sits at alpha 0.001, so the
        // board it froze was the one from GAME LOAD, and it stayed that way
        // for the whole session.
        //
        // A reload has always been right, because both hydrate paths seed
        // currentRound from the stored lastRound. Live entry was the only case
        // that did not, which is the worst kind of split — the bug hides from
        // every test that involves a refresh.
        //
        // REELS ONLY. `wins: []` and `win: 0` are exactly what
        // detectFreeSpinsState builds its reload board with, for the reason it
        // states there: nothing may re-present. The base win has already been
        // paid and shown; handing the free-spins win controller a board with
        // wins on it would celebrate it a second time inside the room.
        //
        // Nothing else is copied either — a bare object carries no
        // mystery_feature, so maskReelForMystery finds none and the board
        // shows the symbols as they finally landed rather than re-masking
        // cells the base game already opened.
        // Same builder every entry path uses — see arrivalBoardFrom.
        const arrival = arrivalBoardFrom(action.payload.round);
        if (arrival) state.currentRound = arrival;
        // ── Where the won package is sitting ──
        //
        // The cell, and only the cell. The reveal itself is over — its doors,
        // its strip and its animation all belong to the base game and none of
        // that survives. What survives is the coordinate, so the room can put
        // the package the player actually won back on the board they won it
        // on.
        //
        // The tier is NOT stored with it on purpose: the ladder is still open
        // at this point, and a gamble can move it. Whoever draws the cell
        // reads the SETTLED tier at the time, so the card on screen is always
        // the package the round is actually being played with.
        state.triggerScatterCell = triggerScatterCellFrom(
          action.payload.round,
        );
        state.awarded = trigger.spins || 0;
        state.played = 0;
        state.spinPlayedBase = 0;
        state.displayPlayedBase = 0;
        state.packageTier = trigger.tier ?? null;
        // The package decides which room the round opens in — see
        // FREE_SPINS_TIER_TO_LEVEL. The collector always starts empty; the
        // level is where you START, not how far into it you are.
        state.level = levelForTier(trigger.tier);
        state.tier = 0;
        // ── The round opens with the trigger spin's win already banked ──
        //
        // Not zero. The server has already folded this win into balance.game,
        // which is what every later response reports back as the round total,
        // so starting at 0 would make the row DROP the moment the first free
        // spin lands and then silently gain it back. Seeding it means the
        // client agrees with the server from the first frame.
        state.triggerWin = action.payload.round?.win || 0;
        state.totalWin = state.triggerWin;
        state.displayedTotalWin = state.triggerWin;
        state.lastWin = 0;
        // The package is won but not yet taken — the gamble round decides
        // whether it is collected as-is, improved, or lost.
        state.gambleSettled = false;
      }

      // RGS parses features.fs_gamble on EVERY command that can carry it — a
      // buy, a trigger spin, or either gamble action — so this one case covers
      // the whole ladder without the caller having to know which it was.
      //
      // Only assigned when the key is present. An undefined `gamble` means
      // "this response said nothing about the gamble", which must not wipe a
      // ladder that is still open.
      if (action.payload?.gamble !== undefined) {
        state.gamble = action.payload.gamble;

        // Landing on tier 0 forfeits the package. Nothing left to collect, so
        // the ladder is over the moment it is reported.
        if (action.payload.gamble?.lost) {
          state.gambleSettled = true;
          state.triggered = false;
          state.awarded = 0;
          state.packageTier = FREE_SPINS_TIER.LOSE;
          // ── Give the seeded totals back too ──
          //
          // The trigger seeds triggerWin/totalWin/displayedTotalWin with the
          // trigger spin's win, so the free-spins round can OPEN with it
          // already banked (see the note above). A lost gamble means that
          // round will never open, so the seed is left with no owner — and
          // WinDisplay reads a leftover displayedTotalWin as "the feature
          // finished": shouldShowFSCompletedWin is
          // `!active && !triggered && displayedTotalWin > 0`, and clearing
          // `triggered` on the line above satisfies the last of those.
          //
          // That is what relabelled a plain base-game win as "Total Win" the
          // moment the gamble dialog closed on a loss. The win itself is not
          // lost with these — the player keeps it, and the base game presents
          // it from game.currentRound.win exactly as it did before the gamble
          // screen opened.
          state.triggerWin = 0;
          state.totalWin = 0;
          state.displayedTotalWin = 0;
        }
      }

      // ── One free spin ──
      //
      // Everything the round needs comes off this one response. Verified live
      // 2026-08-17 — see docs/api-contract-mad-labs-freespins.md.
      if (action.payload?.command === "freespin") {
        // The board, kept apart from game.currentRound so the two rooms never
        // read each other's screen.
        state.currentRound = action.payload.round ?? null;

        const win = action.payload.round?.win || 0;
        state.lastWin = win;

        const features = action.payload.features || {};

        // ── ONE parser, on both paths ──
        //
        // `freeSpinsState` is what RGS.detectFreeSpinsState made of this same
        // response, and it is now published on the spin result as well as the
        // init one. Reading it here retires the second copy of these rules:
        // the reload path and the live path derive `played` from the same
        // line, so they cannot drift. Pot Collection has worked this way all
        // along — featureState.data is its one shape.
        //
        // The raw fields remain as the fallback, because a response can carry
        // the counters while flow.state is not yet a free-spins state (the
        // trigger and the gamble answers), and detectFreeSpinsState returns
        // null for those by design.
        const fsData = action.payload.freeSpinsState?.data || null;

        // ── THE TOTAL IS THE SERVER'S, exactly as free rounds does it ──
        //
        // freeRoundsSlice documents the split and it applies here unchanged:
        //   win           cumulative, from the server (balance.free_rounds)
        //   displayedWin  the presentation-timed figure the player reads,
        //                 advanced when the win is actually shown
        //
        // Ours are totalWin and displayedTotalWin, and `balance.game` is our
        // balance.free_rounds — the same field detectFreeSpinsState already
        // reads for a reload, so live play and recovery now agree by
        // construction.
        //
        // It used to be `totalWin += round.win`, a client-side accumulation
        // held as the source of truth. Free rounds never does that, and for
        // good reason: it drifts from the server on any response the client
        // mis-reads, and it did — while round.win carried the round's total
        // rather than the spin's, every spin added the whole round to itself.
        // An assignment cannot double-count and needs no reload guard, which
        // is the same argument syncFreeSpinsDisplayedWin already makes.
        // The cap, read off the same normalized state as the total. Sticky
        // within a round: once reached it cannot un-reach, and a response that
        // says nothing must not clear it.
        if (fsData?.winLimitReached) state.winLimitReached = true;

        if (typeof fsData?.totalWin === "number") {
          state.totalWin = fsData.totalWin;
        } else {
          // No normalized state on this response (the trigger and the gamble
          // answers): fall back to rolling this spin in, as before.
          state.totalWin += win;
        }

        // COUNTERS FROM THE SERVER, never counted locally. `freespins_issued`
        // GROWS mid-round — reaching a level awards more spins (7 -> 10 was
        // measured) — so a client-side total would end the round early.
        // ── The COUNTER waits for the announcement, the round does not ──
        //
        // `awarded` is committed immediately, as it always was: it is the
        // round's real length, and the spin loop ends the round on it. What is
        // held back is only the number the player READS — see unannouncedSpins.
        //
        // The whole increase, not just the level's share: a spin that levels up
        // can also drop +FS scatters, and both are announced together in the one
        // popup, so both have to wait for it.
        //
        // ── Held for a FEATURE too, not only a level-up ──
        //
        // This used to bail out when `pendingServerLevel` was null, because the
        // room commit was the only thing that released it. That left the whole
        // fault in place for the commoner case: the Golden Mystery's own +FS
        // cells award spins at the TOP of the ladder, where the server reports
        // no new level at all — measured live, `spins -> popup level=none
        // featureSpins=2`, and the button climbed 8 -> 10 the instant the
        // response landed, long before the popup said so.
        //
        // What decides the beat is the ANNOUNCEMENT, and both kinds of spin are
        // announced by the same popup, so both are held the same way. The
        // release is now the announcement itself — see announceFreeSpins.
        const holdNewSpins = (next) => {
          if (typeof next !== "number" || next <= state.awarded) return;
          state.unannouncedSpins = next - state.awarded;
        };

        // ── A hold lasts one spin, at most ──
        //
        // Every hold above is released by the popup that announces it, and the
        // popup is up before the next spin is sent. So anything still held when
        // the NEXT response lands was never announced — the client could not
        // explain the server's increase — and its moment has gone. Cleared here
        // rather than carried, because a stale hold makes the button read low
        // for the rest of the round, which is worse than the early climb this
        // whole mechanism exists to stop.
        state.unannouncedSpins = 0;

        if (typeof fsData?.issued === "number" && fsData.issued > 0) {
          holdNewSpins(fsData.issued);
          state.awarded = fsData.issued;
          state.played = fsData.played;
        } else {
          if (typeof features.freespins_issued === "number") {
            holdNewSpins(features.freespins_issued);
            state.awarded = features.freespins_issued;
          }
          if (typeof features.freespins_left === "number") {
            state.played = Math.max(0, state.awarded - features.freespins_left);
          }
        }

        // ── The counter holds through this spin, and only through this one ──
        //
        // Deliberately NOT re-latched here: the spin whose response this is, is
        // still on screen, and spinPlayedBase is what keeps it counted (see the
        // field). The next spin's start moves it.
        //
        // The exception is the end of the round. With nothing left to play there
        // is no spin to count, so the base catches up and the button reads zero
        // — which is the one moment zero is true.
        // Deliberately NOT displayPlayedBase — the one place the two part
        // company. The button already reached zero when this spin STARTED, so
        // it has nothing left to catch up; touching it here would only undo
        // the lead that makes the last spin read 0. See the field.
        if (state.played >= state.awarded) {
          state.spinPlayedBase = state.played;
        }

      }

      // ── The package taken: `collect_freespins` ──
      //
      // The response that settles the ladder reports the count outright as
      // `features.freespins_issued` and moves flow.state to "freespins"
      // (verified live 2026-08-14). BOTH are required: the count alone also
      // appears on responses that merely describe a round already running,
      // and re-reading it there would reset `played` mid-round.
      //
      // `active` is deliberately NOT raised here. Taking the package and
      // ENTERING the room are two different moments — the intro popup sits
      // between them, and the rooms swap when it hands over.
      const issued = action.payload?.features?.freespins_issued;
      if (
        typeof issued === "number" &&
        issued > 0 &&
        action.payload?.flowState === "freespins" &&
        !state.gambleSettled
      ) {
        state.awarded = issued;
        state.played = 0;
        state.spinPlayedBase = 0;
        state.displayPlayedBase = 0;
        // Back to the trigger win, NOT to zero — the same reason it was seeded
        // at the trigger. The player took the package; the win they arrived
        // with is still theirs and is still inside balance.game.
        state.totalWin = state.triggerWin;
        state.displayedTotalWin = state.triggerWin;
        state.lastWin = 0;
        state.tier = 0;
        // The tier the ladder finished on is the package actually taken,
        // which is not necessarily the one the scatter first revealed.
        const settledTier = state.gamble?.tier ?? state.packageTier;
        state.packageTier = settledTier;
        state.level = levelForTier(settledTier);
        state.gambleSettled = true;
        state.triggered = true;
      }
    });
  },
});

// ============================================================================
// SELECTORS — all PLURAL. See the header note.
// ============================================================================

// ── Lifecycle ──
export const selectFreeSpinsTriggered = (state) => state.freeSpins.triggered;
export const selectFreeSpinsActive = (state) => state.freeSpins.active;
export const selectFreeSpinsSpinning = (state) => state.freeSpins.spinning;
export const selectFreeSpinsSpinComplete = (state) =>
  state.freeSpins.spinComplete;

// ── The package ──
export const selectFreeSpinsAwarded = (state) => state.freeSpins.awarded;
export const selectFreeSpinsPlayed = (state) => state.freeSpins.played;
/** The gamble package tier (0/1/3/5) — NOT the level tier. */
export const selectFreeSpinsPackageTier = (state) =>
  state.freeSpins.packageTier;

/** Spins still to play. Never negative, however the two counts drift. */
/**
 * Has this round hit the win cap?
 *
 * Raised from the response — the server's own `win_limit_reached` when it
 * sends one, otherwise the adapter's `totalWin >= bet x MAX_WIN_MULTIPLIER`.
 * See detectWinLimitReached in PopiPlay/RGS.
 *
 * It ENDS THE ROUND. The loop refuses to schedule another spin once this is
 * true and goes straight to the MAX WIN outro, however many spins were left —
 * see isFreeSpinsRoundFinished.
 */
export const selectFreeSpinsWinLimitReached = (state) =>
  state.freeSpins.winLimitReached;

/**
 * The number on the button: spins left, COUNTING the one on screen.
 *
 * Measured from spinPlayedBase, not from `played` — see that field. It reaches
 * zero only when the round has nothing left to play, never while a spin is
 * still being watched.
 */
export const selectFreeSpinsRemaining = (state) =>
  Math.max(
    0,
    state.freeSpins.awarded -
      Math.min(state.freeSpins.spinPlayedBase, state.freeSpins.played),
  );

/**
 * The same count, as the PLAYER has been told it.
 *
 * Spins a level-up has just awarded are subtracted until the popup announces
 * them — see unannouncedSpins. Between the response landing and that moment the
 * two selectors disagree, and they are meant to: this one is a promise made to
 * the player, and the other is the round's real length.
 *
 * FOR DISPLAY ONLY. Nothing that decides whether to spin again may read it: the
 * loop ends the round when its count reaches zero, so a held spin would open the
 * total-win screen on the very spin that earned more.
 */
export const selectFreeSpinsRemainingDisplay = (state) =>
  Math.max(
    0,
    state.freeSpins.awarded -
      state.freeSpins.unannouncedSpins -
      // NOT Math.min(base, played) — the loop's selector clamps that way, and
      // doing it here would cancel the +1 a starting spin adds (played has not
      // caught up yet) and put the counter back a spin behind.
      state.freeSpins.displayPlayedBase,
  );

/** True on the final spin of the package — the outro hangs off this. */
export const selectIsLastFreeSpin = (state) =>
  state.freeSpins.awarded > 0 &&
  state.freeSpins.played >= state.freeSpins.awarded - 1;

// ── The level ladder ──
export const selectFreeSpinsLevel = (state) => state.freeSpins.level;
export const selectFreeSpinsTier = (state) => state.freeSpins.tier;
/** What the server has already moved but the player has not been shown yet. */
export const selectFreeSpinsPendingTier = (state) => state.freeSpins.pendingTier;
export const selectFreeSpinsPendingServerLevel = (state) =>
  state.freeSpins.pendingServerLevel;
export const selectFreeSpinsPendingLevelUpSpins = (state) =>
  state.freeSpins.pendingLevelUpSpins;

/** The same spins, split one entry per level crossed. */
export const selectFreeSpinsPendingLevelUpSpinsList = (state) =>
  state.freeSpins.pendingLevelUpSpinsList;

/**
 * The multiplier a server rung holds — the number the level-up popup shows.
 *
 *   fs_state.level   1    2    3    4    5
 *   multiplier       x1   x2   x3   x5   x10
 *
 * The popup only ships clips for 3, 5 and 10, so a jump to rung 2 (x2) has no
 * artwork of its own and falls back inside the popup controller.
 */
export const FREE_SPINS_LEVEL_MULTIPLIERS = [1, 2, 3, 5, 10];
export const multiplierForServerLevel = (level) =>
  FREE_SPINS_LEVEL_MULTIPLIERS[
    Math.min(Math.max((typeof level === "number" ? level : 1) - 1, 0), 4)
  ];
/** The server's raw fs_state.level. Drives the PROGRESS BOXES — see the field. */
export const selectFreeSpinsServerLevel = (state) =>
  state.freeSpins.serverLevel;
export const selectFreeSpinsEntrySequencePending = (state) =>
  state.freeSpins.entrySequencePending;
export const selectFreeSpinsAwaitingResume = (state) =>
  state.freeSpins.awaitingResume;
/** A reload popup owns the screen, or is about to. Holds win presentation. */
export const selectFreeSpinsReloadPopupPending = (state) =>
  state.freeSpins.reloadPopupPending;

// ── Win ──
/**
 * The free-spin board. NOT game.currentRound — that one belongs to the base
 * game and is overwritten by every response, this one only by a `freespin`.
 */
export const selectFreeSpinsCurrentRound = (state) =>
  state.freeSpins.currentRound;

/**
 * The PAYLINE TABLE, indexed by line id — `options.lines`, exactly what the
 * base game's selectWinningLines returns. NOT this round's wins.
 *
 * The paylines belong to the game, not to a board: the bonus reels pay on the
 * same 50 lines. matchHorizontalWinningLines resolves a win's line path with
 * `winningLines[win.id]`, so pointing this at `currentRound.wins` handed it a
 * win object — undefined for every real line id — and `data.line` came out
 * empty. Line cycling then drew no win boxes, while the global show-all still
 * did, because that phase reads `data.positions` instead.
 */
export const selectFreeSpinsWinningLines = (state) =>
  state.game.winningLines ?? null;

export const selectFreeSpinsTotalWin = (state) => state.freeSpins.totalWin;

/**
 * The base-game win the player carried into the round. See the state field.
 * Only the presentation needs this; the total already contains it.
 */
export const selectFreeSpinsTriggerWin = (state) => state.freeSpins.triggerWin;

/** [reel, row] the won package sits on, on the board the round opens with. */
export const selectFreeSpinsTriggerScatterCell = (state) =>
  state.freeSpins.triggerScatterCell;
/**
 * The total the UI may show. Lags `totalWin` until the spin is presented — see
 * the field's own note. Use this for anything the player reads.
 */
export const selectFreeSpinsDisplayedTotalWin = (state) =>
  state.freeSpins.displayedTotalWin;
export const selectFreeSpinsLastWin = (state) => state.freeSpins.lastWin;

// ── Gamble ──
export const selectGamble = (state) => state.freeSpins.gamble;
export const selectGambleSettled = (state) => state.freeSpins.gambleSettled;

/** Which part of the feature a refresh landed in. "none" on a normal boot. */
export const selectFreeSpinsReloadPhase = (state) =>
  state.freeSpins.reloadPhase;

/** The outcome pool for the NEXT gamble. Empty when none is offered. */
export const selectGambleVariants = (state) =>
  state.freeSpins.gamble?.variants ?? [];

/** The package the player owns right now, as [spins, tier]. */
export const selectGambleCurrent = (state) =>
  state.freeSpins.gamble?.current ?? null;

/** True only while the server still lists outcomes to gamble for. */
export const selectCanGamble = (state) =>
  state.freeSpins.gamble?.canGamble === true;

/** True when the last rung landed on tier 0 and the package is forfeit. */
export const selectGambleLost = (state) =>
  state.freeSpins.gamble?.lost === true;

export const {
  clearFreeSpinsWin,
  setFreeSpinsTriggered,
  setFreeSpinsActive,
  setFreeSpinsSpinning,
  setFreeSpinsManualStop,
  setFreeSpinsSpinComplete,
  setFreeSpinsEntrySequencePending,
  setFreeSpinsAwaitingResume,
  setFreeSpinsReloadPopupPending,
  freeSpinPlayed,
  setFreeSpinsPlayed,
  addFreeSpins,
  setFreeSpinsLevel,
  setFreeSpinsTier,
  commitFreeSpinsProgress,
  commitFreeSpinsRoomLevel,
  commitFreeSpinsServerLevel,
  announceFreeSpins,
  addFreeSpinsWin,
  setFreeSpinsTotalWin,
  setFreeSpinsWinLimitReached,
  syncFreeSpinsDisplayedWin,
  setGambleState,
  setGambleSettled,
  resetFreeSpins,
} = freeSpinsSlice.actions;

export default freeSpinsSlice.reducer;
